import React from 'react'
import { Box, Typography, LinearProgress, Chip } from '@mui/material'
import { useTheme, alpha } from '@mui/material/styles'
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

const statusToProgress = {
  uploading: 0,
  pending: 10,
  parsing: 50,
  ready: 100,
  error: 100,
}

const DocumentProcessingStatus = ({ documents }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  if (!documents || documents.length === 0) return null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
      >
        {t('processing_documents', 'Processing documents...')}
      </Typography>

      {documents.map((doc) => {
        const progress = statusToProgress[doc.status] ?? 0
        const isUploading = doc.status === 'uploading'
        const isReady = doc.status === 'ready'
        const isError = doc.status === 'error'

        return (
          <Box
            key={doc.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isReady ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
            ) : isError ? (
              <ErrorIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
            ) : isUploading ? (
              <CloudUploadIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
            ) : (
              <DescriptionIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
            )}

            <Typography
              variant="caption"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 160,
                fontWeight: 500,
              }}
            >
              {doc.filename}
            </Typography>

            {!isReady && !isError && (
              <Box sx={{ flex: 1, maxWidth: 120 }}>
                <LinearProgress
                  variant={isUploading ? 'indeterminate' : 'determinate'}
                  value={isUploading ? undefined : progress}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            )}

            <Chip
              label={t(`doc_status_${doc.status}`, doc.status)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                backgroundColor: isReady
                  ? alpha(theme.palette.success.main, 0.1)
                  : isError
                    ? alpha(theme.palette.error.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.1),
                color: isReady
                  ? theme.palette.success.main
                  : isError
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
              }}
            />
          </Box>
        )
      })}
    </Box>
  )
}

export default DocumentProcessingStatus
